/**
 * UNHCR Displacement data source.
 * Fetches refugee/IDP statistics from the UNHCR Population Statistics API.
 * Free, no API key required.
 */

import type { Source, SourceMeta, ConfigField, SourceStatus, NormalizedEvent, Severity } from '../base-source';
import { getApiBase } from '@/app/api-router';
import { getCountryCentroid } from '@/config/country-centroids';

const SOURCE_ID = 'displacement';

interface DisplacementRecord {
  country: string;
  countryCode: string;
  refugees: number;
  idps: number;
  asylumSeekers: number;
  total: number;
  year: number;
  lat?: number;
  lng?: number;
}

/** Bilateral origin -> asylum refugee flow (alpha-2 codes). */
interface DisplacementFlow {
  originCode: string;
  originName: string;
  asylumCode: string;
  asylumName: string;
  refugees: number;
}

interface DisplacementResponse {
  records: DisplacementRecord[];
  flows?: DisplacementFlow[];
  lastUpdated: string;
}

function displacementToSeverity(total: number): Severity {
  if (total >= 5_000_000) return 'critical';
  if (total >= 1_000_000) return 'high';
  if (total >= 100_000) return 'medium';
  if (total >= 10_000) return 'low';
  return 'info';
}

function formatNumber(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}K`;
  return String(n);
}

let status: SourceStatus = {
  state: 'initializing',
  eventCount: 0,
};

export const displacementSource: Source = {
  id: SOURCE_ID,

  meta: {
    name: 'UNHCR Displacement',
    description: 'Refugee and internally displaced person statistics',
    icon: 'users',
    category: 'social',
    updateInterval: 86400_000, // 24 hours (data updates infrequently)
    requiresApiKey: false,
    dataLicense: 'UNHCR Open Data',
    builtIn: true,
  } satisfies SourceMeta,

  configSchema: [] as ConfigField[],

  async initialize(): Promise<void> {
    status = { state: 'initializing', eventCount: 0 };
  },

  async start(): Promise<void> {
    status.state = 'active';
    console.log('[Sentinel] Displacement source started');
  },

  async stop(): Promise<void> {
    status.state = 'disabled';
  },

  async fetch(): Promise<NormalizedEvent[]> {
    try {
      const url = `${getApiBase()}/api/data/displacement`;
      const response = await fetch(url, { signal: AbortSignal.timeout(20_000) });

      if (!response.ok) {
        throw new Error(`Displacement API returned ${response.status}`);
      }

      const data: DisplacementResponse = await response.json();

      if (!data.records || !Array.isArray(data.records)) {
        return [];
      }

      const events: NormalizedEvent[] = [];

      for (const record of data.records) {
        if (record.total <= 0) continue;

        const parts: string[] = [];
        if (record.refugees > 0) parts.push(`${formatNumber(record.refugees)} refugees`);
        if (record.idps > 0) parts.push(`${formatNumber(record.idps)} IDPs`);
        if (record.asylumSeekers > 0) parts.push(`${formatNumber(record.asylumSeekers)} asylum seekers`);

        // Skip records without coordinates — don't place at (0,0)
        if (record.lat == null || record.lng == null) continue;

        events.push({
          id: `displacement-${record.countryCode}-${record.year}`,
          source: SOURCE_ID,
          timestamp: new Date(`${record.year}-01-01`).toISOString(),
          type: 'displacement',
          title: `${record.country}: ${formatNumber(record.total)} displaced (${parts.join(', ')})`,
          severity: displacementToSeverity(record.total),
          location: {
            lat: record.lat,
            lng: record.lng,
            name: record.country,
            country: record.country,
            countryCode: record.countryCode,
          },
          tags: ['displacement', 'unhcr', record.countryCode.toLowerCase()],
          raw: {
            refugees: record.refugees,
            idps: record.idps,
            asylumSeekers: record.asylumSeekers,
            total: record.total,
            year: record.year,
          },
        });
      }

      // Bilateral flows — feed the arc layer (raw.sourceCoord/targetCoord,
      // [lng, lat] order as the deck.gl accessors expect)
      for (const flow of data.flows ?? []) {
        const origin = getCountryCentroid(flow.originCode);
        const asylum = getCountryCentroid(flow.asylumCode);
        if (!origin || !asylum) continue;

        events.push({
          id: `displacement-flow-${flow.originCode}-${flow.asylumCode}`,
          source: SOURCE_ID,
          timestamp: new Date().toISOString(),
          type: 'displacement',
          title: `${flow.originName} → ${flow.asylumName}: ${formatNumber(flow.refugees)} refugees`,
          severity: displacementToSeverity(flow.refugees),
          location: {
            lat: asylum[0],
            lng: asylum[1],
            name: flow.asylumName,
            countryCode: flow.asylumCode,
          },
          tags: ['displacement', 'unhcr', 'flow', flow.originCode.toLowerCase(), flow.asylumCode.toLowerCase()],
          raw: {
            sourceCoord: [origin[1], origin[0]],
            targetCoord: [asylum[1], asylum[0]],
            displacedCount: flow.refugees,
            originCode: flow.originCode,
            asylumCode: flow.asylumCode,
          },
        });
      }

      status.state = 'active';
      status.lastFetch = new Date().toISOString();
      status.eventCount = events.length;
      return events;
    } catch (err) {
      status.state = 'error';
      status.lastError = err instanceof Error ? err.message : String(err);
      throw err;
    }
  },

  getStatus(): SourceStatus {
    return { ...status };
  },
};
